import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from 'react-helmet-async';

import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import SkipLink from "@/components/SkipLink";
import { Suspense, lazy } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import { TranslationProvider, Language } from "@/hooks/useTranslations";
import { useApplyTypography } from "@/hooks/useTypography";
import useAnalytics from "@/hooks/useAnalytics";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { usePageTracking } from "@/hooks/usePageTracking";
import { SessionIntelligenceProvider, useSessionIntelligence } from "@/hooks/useSessionIntelligence";
import { KPConfigProvider } from "@/hooks/useKPConfig";
import PageLoader from "@/components/PageLoader";
import PageTransition from "@/components/PageTransition";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load non-critical pages
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const SubscriptionAction = lazy(() => import("./pages/SubscriptionAction"));
const AllPosts = lazy(() => import("./pages/AllPosts"));
const Podcast = lazy(() => import("./pages/Podcast"));
const PodcastShownotes = lazy(() => import("./pages/PodcastShownotes"));
const Method = lazy(() => import("./pages/Method"));
const Speaking = lazy(() => import("./pages/Speaking"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const SitemapXml = lazy(() => import("./pages/SitemapXml"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const SleepScan = lazy(() => import("./pages/SleepScan"));
const SleepScanLP = lazy(() => import("./pages/SleepScanLP"));
const RedirectHandler = lazy(() => import("./pages/RedirectHandler"));
const IntelligenceDashboard = lazy(() => import("./components/admin/IntelligenceDashboard"));
const Lexicon = lazy(() => import("./pages/LexiconEnhanced"));
const LexiconDetail = lazy(() => import("./pages/LexiconDetailEnhanced"));
const ExecutiveRecovery = lazy(() => import("./pages/ExecutiveRecovery"));
const DarmDownload = lazy(() => import("./pages/DarmDownload"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseLearn = lazy(() => import("./pages/CourseLearn"));
const MyCourses = lazy(() => import("./pages/MyCourses"));
const CourseCertificate = lazy(() => import("./pages/CourseCertificate"));
const Book = lazy(() => import("./pages/Book"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
import BookExtra from "./pages/BookExtra";

// Lazy load components with Framer Motion (reduces initial bundle)
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const SleepScanPopup = lazy(() => import("@/components/SleepScanPopup"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const ContentDebugOverlay = lazy(() => import("@/components/AdaptiveContent").then(m => ({ default: m.ContentDebugOverlay })));

const queryClient = new QueryClient();

// Wrapper component to detect language from URL
const LanguageDetector = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const match = location.pathname.match(/^\/(en)/);
  const initialLanguage: Language = match ? 'en' : 'de';

  return (
    <TranslationProvider initialLanguage={initialLanguage}>
      <KPConfigProvider>
        <SessionIntelligenceProvider>
          <TypographyProvider>
            {children}
          </TypographyProvider>
        </SessionIntelligenceProvider>
      </KPConfigProvider>
    </TranslationProvider>
  );
};

// Component to apply typography settings, analytics, and tracking
const TypographyProvider = ({ children }: { children: React.ReactNode }) => {
  const { incrementPageViews } = useSessionIntelligence();
  const location = useLocation();
  
  useApplyTypography();
  useAnalytics(); // Initialize analytics with consent check
  useSessionTracking(); // Initialize first-party session tracking
  usePageTracking(); // Track page views with time-on-page
  
  // Update session intelligence on route change
  useEffect(() => {
    incrementPageViews();
  }, [location.pathname, incrementPageViews]);
  
  return <>{children}</>;
};

// App routes component
const AppRoutes = () => {
  return (
    <PageTransition>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* German routes (default, no prefix) - with German URL slugs */}
          <Route path="/" element={<Index />} />
          <Route path="/posts" element={<AllPosts />} />
          <Route path="/podcast" element={<Podcast />} />
          <Route path="/podcast/:slug" element={<PodcastShownotes />} />
          <Route path="/system" element={<Method />} />
          <Route path="/programme" element={<Speaking />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/sitemap.xml" element={<SitemapXml />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/ansatz" element={<About />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/blog" element={<AllPosts />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/:section" element={<Admin />} />
          <Route path="/admin/intelligence" element={<IntelligenceDashboard />} />
          <Route path="/sleep-scan" element={<SleepScan />} />
          <Route path="/lp/sleep-scan" element={<SleepScanLP />} />
          <Route path="/lexikon" element={<Lexicon />} />
          <Route path="/lexikon/:slug" element={<LexiconDetail />} />
          <Route path="/executive-recovery" element={<ExecutiveRecovery />} />
          <Route path="/darm" element={<DarmDownload />} />
          <Route path="/subscription/confirm" element={<SubscriptionAction action="confirm" />} />
          <Route path="/subscription/unsubscribe" element={<SubscriptionAction action="unsubscribe" />} />
          <Route path="/kurse" element={<Courses />} />
          <Route path="/kurse/:slug" element={<CourseDetail />} />
          <Route path="/kurse/:slug/lernen" element={<CourseLearn />} />
          <Route path="/kurse/:slug/zertifikat" element={<CourseCertificate />} />
          <Route path="/meine-kurse" element={<MyCourses />} />
          <Route path="/buch" element={<Book />} />
          <Route path="/buch/:slug" element={<BookExtra />} />
          <Route path="/datenschutz" element={<Navigate to="/privacy" replace />} />
          <Route path="/wissen" element={<Knowledge />} />
          {/* English routes (with /en prefix) */}
          <Route path="/en" element={<Index />} />
          <Route path="/en/posts" element={<AllPosts />} />
          <Route path="/en/blog" element={<AllPosts />} />
          <Route path="/en/podcast" element={<Podcast />} />
          <Route path="/en/podcast/:slug" element={<PodcastShownotes />} />
          <Route path="/en/system" element={<Method />} />
          <Route path="/en/speaking" element={<Speaking />} />
          <Route path="/en/terms" element={<Terms />} />
          <Route path="/en/privacy" element={<Privacy />} />
          <Route path="/en/sitemap" element={<Sitemap />} />
          <Route path="/en/search" element={<SearchResults />} />
          <Route path="/en/about" element={<About />} />
          <Route path="/en/contact" element={<Contact />} />
          <Route path="/en/blog/:slug" element={<BlogDetail />} />
          <Route path="/en/auth" element={<Auth />} />
          <Route path="/en/admin" element={<Admin />} />
          <Route path="/en/admin/:section" element={<Admin />} />
          <Route path="/en/sleep-scan" element={<SleepScan />} />
          <Route path="/en/lp/sleep-scan" element={<SleepScanLP />} />
          <Route path="/en/lexikon" element={<Lexicon />} />
          <Route path="/en/lexikon/:slug" element={<LexiconDetail />} />
          <Route path="/en/executive-recovery" element={<ExecutiveRecovery />} />
          <Route path="/en/courses" element={<Courses />} />
          <Route path="/en/courses/:slug" element={<CourseDetail />} />
          <Route path="/en/courses/:slug/learn" element={<CourseLearn />} />
          <Route path="/en/courses/:slug/certificate" element={<CourseCertificate />} />
          <Route path="/en/my-courses" element={<MyCourses />} />
          <Route path="/en/book" element={<Book />} />
          <Route path="/en/knowledge" element={<Knowledge />} />
          {/* Catch-all route: Check for redirects first, then show 404 */}
          <Route path="*" element={<RedirectHandler />} />
        </Routes>
      </Suspense>
    </PageTransition>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <SkipLink />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <LanguageDetector>
              <AppRoutes />
              {/* Lazy-loaded popups - don't block initial render */}
              <Suspense fallback={null}>
                <SleepScanPopup />
                <ExitIntentPopup />
                <CookieConsent />
                <ContentDebugOverlay />
              </Suspense>
            </LanguageDetector>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;